import {
  CLARITY_OPTIONS,
  COLOR_OPTIONS,
  CUT_OPTIONS,
  DEFAULT_DIAMOND_SEARCH_INPUT,
} from '@shared/lib/constants';
import { capitalize, parseFloatOrNull } from '@shared/lib/utils';
import { DiamondSearchInput, DiamondSearchParams } from './types';

export const markUpPrice = (price: number) => {
  let displayPrice = price;

  // Step 1: Apply margin only to the portion of the cost price within each bracket
  // if (price < 100) {
  //   displayPrice = 5 * price;
  // } else if (100 <= price && price < 250) {
  //   displayPrice = 100 * 5 + (price - 100) * 4.5;
  // } else if (250 <= price && price < 500) {
  //   displayPrice = 100 * 5 + 150 * 4.5 + (price - 250) * 4;
  // } else if (500 <= price && price < 1000) {
  //   displayPrice = 100 * 5 + 150 * 4.5 + 250 * 4 + (price - 500) * 3.5;
  // } else if (1000 <= price && price < 2000) {
  //   displayPrice = 100 * 5 + 150 * 4.5 + 250 * 4 + 500 * 3.5 + (price - 1000) * 3;
  // } else if (2000 <= price && price < 3000) {
  //   displayPrice = 100 * 5 + 150 * 4.5 + 250 * 4 + 500 * 3.5 + 1000 * 3 + (price - 2000) * 2.5;
  // } else {
  //   displayPrice =
  //     100 * 5 + 150 * 4.5 + 250 * 4 + 500 * 3.5 + 1000 * 3 + 1000 * 2.5 + (price - 3000) * 2;
  // }

  if (displayPrice < 1000) {
    displayPrice = Math.round(displayPrice / 5) * 5;
  } else {
    displayPrice = Math.round(displayPrice / 10) * 10;
  }

  // Step 3: Adjust to customer-friendly numbers
  // a) For prices <= 1000, if they end with 00, lower by 5 to make it end with 95
  if (displayPrice <= 1000 && displayPrice % 100 <= 10) {
    displayPrice = Math.floor(displayPrice / 100) * 100 - 5;

    // # b) For prices > 1000, if they end with 000 or 050, lower to the nearest 990
  } else if (displayPrice > 1000 && displayPrice % 1000 <= 20) {
    displayPrice = Math.floor(displayPrice / 1000) * 1000 - 10;
  }

  return displayPrice;
};

export const reverseMarkUpPrice = (price: number) => {
  // Because we round down to 95 or 90, we need to add 15 or 25 to get the original price
  const reversedPrice = price < 1000 ? price + 15 : price + 25;
  // overnight bug? limit 1000 becomes 800
  return (reversedPrice / 8) * 10;
};

export const encodeLotNumber = (lotNumber: string) => {
  const match = lotNumber.match(/^([A-Za-z]+)(\d+)$/);
  if (!match) {
    throw new Error('Invalid lot number format');
  }
  const [_, leadingChars, numberPart] = match;
  const base32EncodedNumber = parseInt(numberPart || '0', 10)
    .toString(32)
    .toUpperCase();
  return `${leadingChars}-${base32EncodedNumber}`;
};

export const decodeLotNumber = (encodedLotNumber: string) => {
  const match = encodedLotNumber.match(/^([A-Za-z]+)-([A-Z0-9]+)$/);
  if (!match) {
    throw new Error('Invalid encoded lot number format');
  }
  const [_, leadingChars, numberPart] = match;
  const decodedNumber = parseInt(numberPart || '0', 32);
  return `${leadingChars}${decodedNumber}`;
};

export const parseDiamondFilterParams = (searchParams: URLSearchParams): DiamondSearchParams => {
  const defaultInput = DEFAULT_DIAMOND_SEARCH_INPUT;
  const shape = capitalize(searchParams.get('shape') || defaultInput.shape)!;
  const priceMin = parseFloatOrNull(searchParams.get('priceMin')) || defaultInput.priceMin;
  const priceMax = parseFloatOrNull(searchParams.get('priceMax')) || defaultInput.priceMax;

  const clarityMin = searchParams.get('clarityMin') || defaultInput.clarityMin;
  const clarityMax = searchParams.get('clarityMax') || defaultInput.clarityMax;

  const cutMin = searchParams.get('cutMin') || defaultInput.cutMin;
  const cutMax = searchParams.get('cutMax') || defaultInput.cutMax;

  const colorMin = searchParams.get('colorMin') || defaultInput.colorMin;
  const colorMax = searchParams.get('colorMax') || defaultInput.colorMax;

  const caratMin = parseFloatOrNull(searchParams.get('caratMin')) || defaultInput.caratMin;
  let caratMax = parseFloatOrNull(searchParams.get('caratMax')) || defaultInput.caratMax;
  if (caratMax && caratMin && caratMin > caratMax) {
    caratMax = undefined;
  }
  const page = parseInt(searchParams.get('page') || '1', 10);

  const searchInput: DiamondSearchInput = {
    shape,
    clarityMin,
    clarityMax,
    cutMin,
    cutMax,
    colorMin,
    colorMax,
    caratMin,
    caratMax,
    priceMin,
    priceMax,
    page,
  };

  return convertDiamondSearchParams(searchInput);
};

export const convertDiamondSearchParams = (
  searchInput: DiamondSearchInput
): DiamondSearchParams => {
  const { clarityMin, clarityMax, cutMin, cutMax, colorMin, colorMax } = searchInput;
  const clarityMinIdx = clarityMin ? CLARITY_OPTIONS.indexOf(clarityMin) : -1;
  const clarityMaxIdx = clarityMax ? CLARITY_OPTIONS.indexOf(clarityMax) : -1;
  const clarities = CLARITY_OPTIONS.slice(
    clarityMinIdx >= 0 ? clarityMinIdx : 0,
    clarityMaxIdx >= 0 ? clarityMaxIdx + 1 : CLARITY_OPTIONS.length
  );
  const cutMinIdx = cutMin ? CUT_OPTIONS.indexOf(cutMin) : -1;
  const cutMaxIdx = cutMax ? CUT_OPTIONS.indexOf(cutMax) : -1;
  const cuts = CUT_OPTIONS.slice(
    cutMinIdx >= 0 ? cutMinIdx : 0,
    cutMaxIdx >= 0 ? cutMaxIdx + 1 : CUT_OPTIONS.length
  );
  const colorMinIdx = colorMin ? COLOR_OPTIONS.indexOf(colorMin) : -1;
  const colorMaxIdx = colorMax ? COLOR_OPTIONS.indexOf(colorMax) : -1;
  const colors = COLOR_OPTIONS.slice(
    colorMinIdx >= 0 ? colorMinIdx : 0,
    colorMaxIdx >= 0 ? colorMaxIdx + 1 : COLOR_OPTIONS.length
  );

  return {
    ...searchInput,
    shapes: [searchInput.shape],
    clarities,
    cuts,
    colors,
  };
};
